import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import React from "react";
import { shapeShell, shapeFlyout, shapePill, heightCta } from "../theme/shape";

/**
 * Blueprint for MagicFurnish modals: use `StandardDialog` + `mfModalPillButtonSx` on actions,
 * `getStandardDialogPaperSx` (slightly translucent paper so the page shows through), and
 * `standardDialogBackdropSx` (invisible full-screen layer — still catches outside clicks, no dim/blur).
 */

/** Invisible backdrop: no dimming or blur on the page; MUI still uses it for backdrop click + focus trap. */
export const standardDialogBackdropSx = {
  backgroundColor: "transparent",
  backdropFilter: "none",
  WebkitBackdropFilter: "none",
};

/** Paper: ~97% white so content behind the card is faintly visible; no backdrop-filter (cheap compositing). */
export function getStandardDialogPaperSx(theme) {
  return {
    borderRadius: shapeShell,
    overflow: "hidden",
    width: "100%",
    maxWidth: "min(100%, 560px)",
    margin: { xs: "12px", sm: "24px" },
    maxHeight: "calc(100% - 24px)",
    boxShadow: [
      `0 32px 80px ${alpha(theme.palette.common.black, 0.18)}`,
      `0 12px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    ].join(", "),
    backgroundColor: alpha("#ffffff", 0.97),
    border: `1px solid ${alpha("#ffffff", 0.93)}`,
    backgroundImage: "none",
  };
}

/**
 * Editor zone / filter flyouts. Default radius is shapeFlyout (16px) so
 * the panel reads as "of the toolbar family" — 18px bottom pills sit
 * just below, 28px modals are intentionally rounder.
 */
export function getEditorPanelSurfaceSx(theme, { borderRadius = shapeFlyout } = {}) {
  return {
    borderRadius,
    overflow: "hidden",
    backgroundColor: alpha("#ffffff", 0.95),
    border: `1px solid ${alpha("#ffffff", 0.93)}`,
    backgroundImage: "none",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    boxShadow: [
      `0 32px 80px ${alpha(theme.palette.common.black, 0.18)}`,
      `0 12px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    ].join(", "),
  };
}

/**
 * @deprecated Prefer getMFPrimaryButtonSx (utils/dialogStyles) for the
 * brand pill, or apply shape tokens directly. Kept for back-compat with
 * call sites that import this constant.
 */
export const mfModalPillButtonSx = {
  borderRadius: shapePill,
  minHeight: heightCta,
  py: 1.25,
  textTransform: "none",
};

/**
 * Shared dialog shell for MagicFurnish. Centralize visual updates here so
 * consumers (dashboard modals, and later admin) inherit the same design.
 */
export default function StandardDialog({
  open,
  onClose,
  title,
  subheader,
  children,
  footer,
  headerStart,
  /** `center`: single primary CTA centered (narrow max width). `stretch`: full-width stacked actions. */
  footerLayout = "stretch",
  maxWidth = "sm",
  fullWidth = true,
  PaperProps,
  BackdropProps,
  disableEscapeKeyDown,
  idTitle = "mf-standard-dialog-title",
}) {
  const theme = useTheme();
  const paperBase = getStandardDialogPaperSx(theme);
  const titleRowAlignItems = headerStart ? "flex-start" : "center";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      scroll="paper"
      aria-labelledby={idTitle}
      disableEscapeKeyDown={disableEscapeKeyDown}
      BackdropProps={{
        ...BackdropProps,
        sx: [
          standardDialogBackdropSx,
          ...(Array.isArray(BackdropProps?.sx)
            ? BackdropProps.sx
            : BackdropProps?.sx
              ? [BackdropProps.sx]
              : []),
        ],
      }}
      PaperProps={{
        ...PaperProps,
        sx: [paperBase, PaperProps?.sx],
      }}
    >
      <DialogTitle
        id={idTitle}
        sx={{
          position: "relative",
          m: 0,
          pt: 2.5,
          pb: subheader ? 1 : 2,
          pl: 2.5,
          pr: 7,
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction="row"
          alignItems={titleRowAlignItems}
          spacing={1}
          sx={{ pr: 0, minHeight: 36 }}
        >
          {headerStart}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 600,
              fontSize: { xs: "1.0625rem", sm: "1.125rem" },
              lineHeight: 1.35,
              textAlign: "left",
              color: "text.primary",
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              pt: headerStart ? 0.125 : 0,
            }}
          >
            {title}
          </Typography>
        </Stack>
        {subheader ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, textAlign: "left", lineHeight: 1.5, pr: 0 }}
          >
            {subheader}
          </Typography>
        ) : null}
        <IconButton
          aria-label="Close dialog"
          onClick={(e) => onClose?.(e, "backdropClick")}
          size="small"
          sx={{
            position: "absolute",
            right: 20,
            top: 20,
            color: "text.secondary",
            "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.08) },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      {children != null ? (
        <DialogContent
          sx={{
            px: 2.5,
            pt: 0,
            pb: 2,
            "&.MuiDialogContent-root": { paddingTop: 0 },
          }}
        >
          {children}
        </DialogContent>
      ) : null}

      {footer != null ? (
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2.5,
            pt: 0,
            flexDirection: "column",
            alignItems: footerLayout === "center" ? "center" : "stretch",
            gap: 1,
          }}
        >
          {footerLayout === "center" ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 1,
                }}
              >
                {footer}
              </Box>
            </Box>
          ) : (
            footer
          )}
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

StandardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.node,
  subheader: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  footerLayout: PropTypes.oneOf(["stretch", "center"]),
  headerStart: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  fullWidth: PropTypes.bool,
  PaperProps: PropTypes.object,
  BackdropProps: PropTypes.object,
  disableEscapeKeyDown: PropTypes.bool,
  idTitle: PropTypes.string,
};
